/**
 * Custom connector (collector) script tools.
 *
 * Form control, not mutation: `set_collector_script` fills the open editor and
 * stops there. Nothing reaches the server until the operator reads the code and
 * clicks Save themselves — the script runs as the driverhost's own user, so it
 * is the one thing on this gateway that must never be applied unread.
 *
 * Both tools go through collectorEditorBridge and fail closed when no editor is
 * open, so the model is told to ask the operator to open one instead of
 * silently writing nowhere.
 */

import {
  applyCollectorScript,
  readCollectorEditor,
} from "../collectorEditorBridge";

const noEditor = (verb) => ({
  ok: false,
  error: {
    code: "NO_COLLECTOR_EDITOR",
    message:
      `No custom connector editor is open, so there is nothing to ${verb}. Ask the operator to ` +
      "go to Connectors → Custom Connectors and open a collector (or click New collector), " +
      "then try again.",
  },
});

export const get_collector_script = {
  name: "get_collector_script",
  description:
    "Read the custom connector (collector) open in the gateway's editor right now: its name, " +
    "description, params, env keys, declared outputs and the FULL Python script as it currently " +
    "stands — the operator's manual edits included. Call this before writing or changing a script " +
    "so you edit the text that is actually on screen instead of a remembered copy, and call it " +
    "again on every follow-up request. Fails when no collector editor is open.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: async () => {
    const snapshot = readCollectorEditor();
    if (!snapshot) return noEditor("read");
    return { ok: true, data: snapshot };
  },
};

export const set_collector_script = {
  name: "set_collector_script",
  description:
    "Put a Python script into the open custom connector editor, and optionally suggest the outputs " +
    "it returns. This ONLY fills the editor — nothing is saved and nothing runs. The operator reads " +
    "the code and clicks Save; suggested outputs sit in the outputs table marked as suggestions " +
    "until they accept or discard each one, so say so in your answer.\n" +
    "Always call `get_collector_script` first and send the COMPLETE script, never a fragment or a " +
    "diff — the whole editor is replaced.\n" +
    "The script must: run on Python 3.10+ with the STANDARD LIBRARY ONLY (`urllib.request`, `json`, " +
    "`datetime`, `csv`, `re`, …) because it runs under `python -I` with no site-packages and no pip; " +
    "give every network call an explicit timeout; read one JSON object from stdin " +
    "(`{\"params\", \"now\", \"previous\"}`); print exactly ONE JSON object to stdout " +
    "(`{\"values\": {...}}`, values being numbers, strings or booleans); send every diagnostic to " +
    "stderr; and never contain a secret — read one from `os.environ` and tell the operator to add " +
    "that variable to Env keys.\n" +
    "`outputs` entries are `{key, tag_path?, type?}`; `key` must match a key the script puts in " +
    "`values`, and an omitted `tag_path` is filled in from the collector's name.",
  parameters: {
    type: "object",
    properties: {
      script: { type: "string", minLength: 1 },
      outputs: {
        type: "array",
        items: {
          type: "object",
          properties: {
            key: { type: "string", minLength: 1 },
            tag_path: { type: "string" },
            type: { type: "string", enum: ["bool", "int64", "float64", "string"] },
          },
          required: ["key"],
          additionalProperties: false,
        },
      },
    },
    required: ["script"],
    additionalProperties: false,
  },
  execute: async ({ script, outputs }) => {
    const result = applyCollectorScript({ script, outputs });
    if (!result) return noEditor("write");
    return result;
  },
};

const collectorScriptTools = [get_collector_script, set_collector_script];

export default collectorScriptTools;
